// MainLandingDesk.jsx — DRHP Workspace landing / command centre.
// 1:1 port of drhp_workspace_main_landing.html, scoped under `.drhp-land-root`.
// Hydrates the completion ring, KPI row, chapters list and team activity from
// the live /api/drhp-workspace endpoints. Quick-action cards switch the phase.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RING_C = 201; // 2·π·32

const dotForStatus = (s) =>
  s === "done" || s === "approved" ? "cs-done"
  : s === "review" ? "cs-rev"
  : s === "empty" || !s ? "cs-empty"
  : "cs-prog";

const OWNER_PALETTE = [
  { bg: "#fdecec", color: "#c0392b" }, { bg: "#e7f6ec", color: "#16794a" },
  { bg: "#efebfd", color: "#6C4BF4" }, { bg: "#e6f1fb", color: "#0c447c" },
];
const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "—";

const actDot = (action) =>
  ["approve", "sign-off"].includes(action) ? "#16a34a"
  : action === "sync" || action === "import" || action === "export" ? "#d97706"
  : action === "edit" || action === "delete" ? "#6C4BF4"
  : "#16a34a";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

export default function MainLandingDesk({ workspace, onNavigate, onExport, exporting }) {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState({ total: 2970, autofillable: 2108, filled: 0, empty: 0, completeness: 0 });
  const [company, setCompany] = useState({ name: "Your company", exch: "" });
  const [chapters, setChapters] = useState([]);
  const [activity, setActivity] = useState([]);
  const [mismatches, setMismatches] = useState(0);
  const [blockers, setBlockers] = useState({ missingMandatory: 0, missingTotal: 0, items: [], pct: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await api.getMaster(workspace);
        if (!alive) return;
        if (m.kpis) setKpis(m.kpis);
        setCompany({ name: m.company?.name || "Your company", exch: m.issue?.exch || "" });
      } catch {}
      try {
        const cs = await api.chapters();
        if (alive) setChapters(cs || []);
      } catch {}
      try {
        const a = await api.auditRecent(workspace);
        if (alive) setActivity(a || []);
      } catch {}
      try {
        const lm = await api.linkedMap(workspace);
        if (alive) setMismatches((lm.fields || []).reduce((s, f) => s + (f.stats?.mismatch || 0), 0));
      } catch {}
      try {
        const r = await fetch(`${API_URL}/api/workspace/vault/blockers`, { credentials: "include" });
        const b = await r.json();
        if (alive && b) setBlockers(b);
      } catch {}
    })();
    return () => { alive = false; };
  }, [workspace]);

  const reviewCount = chapters.filter((c) => c.status === "review").length;
  const inProgress = chapters.filter((c) => ["draft", "in_progress", "progress"].includes(c.status)).length;
  const pct = Math.min(100, Math.max(0, kpis.completeness || 0));
  const dashoffset = Math.round(RING_C - RING_C * (pct / 100));

  return (
    <div className="drhp-land-root" data-testid="drhp-landing-desk">
      <div className="ws-bar">
        <div className="ws-logo"><i className="ti ti-file-text" style={{ color: "#fff", fontSize: 17 }} /></div>
        <div>
          <div className="ws-title">DRHP Workspace</div>
          <div className="ws-sub-t">Collaborative prospectus drafting</div>
        </div>
        <div className="ws-co">
          <span className="ws-co-n">{company.name}</span>
          <span className="ws-co-tag">{company.exch}</span>
          <button className="ws-export" onClick={onExport} disabled={exporting} data-testid="landing-export-btn">
            <i className="ti ti-file-export" style={{ fontSize: 12 }} /> {exporting ? "Preparing…" : "Export DRHP"}
          </button>
        </div>
      </div>

      <div className="hero">
        <div className="hero-l">
          <div className="hero-h">DRHP command centre</div>
          <div className="hero-d">
            Your DRHP is {pct}% complete. Master data has auto-filled <b style={{ color: "#fff" }}>{kpis.filled.toLocaleString()}</b> fields —
            {" "}{kpis.empty.toLocaleString()} still open{mismatches ? ` and ${mismatches} mismatch(es) need fixing` : ""} before your next SEBI checkpoint.
          </div>
        </div>
        <div className="ring-wrap">
          <svg className="ring" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="32" fill="none" stroke="#34343f" strokeWidth="7" />
            <circle cx="40" cy="40" r="32" fill="none" stroke="#a892fb" strokeWidth="7"
              strokeDasharray={RING_C} strokeDashoffset={dashoffset} strokeLinecap="round" transform="rotate(-90 40 40)" />
            <text x="40" y="45" textAnchor="middle" fontSize="17" fontWeight="600" fill="#fff" fontFamily="DM Sans">{pct}%</text>
          </svg>
          <div className="ring-c">Overall<br /><b>completion</b></div>
        </div>
      </div>

      <div className="kpis">
        <div className="kk"><div className="kk-v p" data-testid="kpi-chapters">{chapters.length || 39}</div><div className="kk-l">Chapters</div></div>
        <div className="kk"><div className="kk-v">{kpis.total.toLocaleString()}</div><div className="kk-l">Total placeholders</div></div>
        <div className="kk"><div className="kk-v g">{kpis.filled.toLocaleString()}</div><div className="kk-l">Auto-filled from master</div></div>
        <div className="kk"><div className="kk-v a">{kpis.empty.toLocaleString()}</div><div className="kk-l">Fields still open</div></div>
        <div className="kk"><div className="kk-v r">{mismatches}</div><div className="kk-l">Data mismatches</div></div>
      </div>

      <div className="body">
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-t"><i className="ti ti-bolt" style={{ color: "#6C4BF4" }} /> Quick actions</div>
            <div className="qa-grid">
              <div className="qa" onClick={() => onNavigate("DATA_COLLECTION")} data-testid="qa-master-data">
                <div className="qa-ic"><i className="ti ti-database" /></div>
                <div className="qa-h">Master Data</div>
                <div className="qa-d">Capture company, promoters, advisors once</div>
                <span className="qa-badge b-a">{kpis.empty.toLocaleString()} fields open</span>
              </div>
              <div className="qa" onClick={() => onNavigate("LINKED_MAP")} data-testid="qa-linked-map">
                <div className="qa-ic"><i className="ti ti-link" /></div>
                <div className="qa-h">Linked Fields Map</div>
                <div className="qa-d">Verify auto-population, fix mismatches</div>
                <span className={`qa-badge ${mismatches ? "b-r" : "b-g"}`}>{mismatches ? `${mismatches} mismatches` : "All synced"}</span>
              </div>
              <div className="qa" onClick={() => onNavigate("DRAFTING")} data-testid="qa-draft">
                <div className="qa-ic"><i className="ti ti-edit" /></div>
                <div className="qa-h">Draft chapters</div>
                <div className="qa-d">Co-edit with tracked changes &amp; comments</div>
                <span className="qa-badge b-p">{inProgress} in progress</span>
              </div>
              <div className="qa" onClick={() => onNavigate("LINKED_MAP")} data-testid="qa-consistency">
                <div className="qa-ic"><i className="ti ti-checkup-list" /></div>
                <div className="qa-h">Consistency check</div>
                <div className="qa-d">Reconcile figures &amp; names before filing</div>
                <span className="qa-badge b-g">Run check</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-t"><i className="ti ti-list-check" style={{ color: "#6C4BF4" }} /> Chapters
              <span className="more" onClick={() => onNavigate("DRAFTING")} data-testid="landing-view-all-chapters">View all {chapters.length || 39} →</span>
            </div>
            {(chapters.slice(0, 6)).map((c, i) => (
              <div className="chap" key={c.id} onClick={() => onNavigate("DRAFTING")} data-testid={`landing-chapter-${c.id}`}>
                <span className={`chap-dot ${dotForStatus(c.status)}`} />
                <span className="chap-n">{c.title}</span>
                <span className="chap-meta">{c.status || "not started"}</span>
                <span className="chap-own" style={{ background: OWNER_PALETTE[i % 4].bg, color: OWNER_PALETTE[i % 4].color }}>
                  {initials(c.owner || c.editor || c.last_editor)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-t"><i className="ti ti-alert-circle" style={{ color: "#dc2626" }} /> Needs your attention</div>
            {blockers.missingMandatory > 0 && (
              <div className="att" onClick={() => navigate("/document-vault")} data-testid="att-filing-blockers">
                <div className="att-ic" style={{ background: "#fdecec", color: "#c0392b" }}><i className="ti ti-file-alert" /></div>
                <div className="att-mid">
                  <div className="att-h">{blockers.missingMandatory} mandatory document(s) missing — filing blocker</div>
                  <div className="att-d">
                    {blockers.items.slice(0, 2).map((i) => i.title).join(", ")}
                    {blockers.items.length > 2 ? "…" : ""} — upload in the Document Vault before filing.
                  </div>
                </div>
                <span className="att-go">Upload →</span>
              </div>
            )}
            <div className="att" onClick={() => onNavigate("LINKED_MAP")} data-testid="att-mismatches">
              <div className="att-ic" style={{ background: "#fdecec", color: "#c0392b" }}><i className="ti ti-alert-triangle" /></div>
              <div className="att-mid"><div className="att-h">{mismatches} data mismatch(es)</div><div className="att-d">Names &amp; figures differ from master data</div></div>
              <span className="att-go">Fix →</span>
            </div>
            <div className="att" onClick={() => onNavigate("DRAFTING")} data-testid="att-signoff">
              <div className="att-ic" style={{ background: "#fdf3e3", color: "#a86711" }}><i className="ti ti-signature" /></div>
              <div className="att-mid"><div className="att-h">{reviewCount} chapter(s) await sign-off</div><div className="att-d">Restated Financials needs auditor attestation</div></div>
              <span className="att-go">Review →</span>
            </div>
            <div className="att" onClick={() => onNavigate("DATA_COLLECTION")} data-testid="att-open-fields">
              <div className="att-ic" style={{ background: "#efebfd", color: "#6C4BF4" }}><i className="ti ti-inbox" /></div>
              <div className="att-mid"><div className="att-h">{kpis.empty.toLocaleString()} fields still open</div><div className="att-d">Capture missing data in Master Data</div></div>
              <span className="att-go">Open →</span>
            </div>
          </div>

          <div className="card">
            <div className="card-t"><i className="ti ti-activity" style={{ color: "#6C4BF4" }} /> Team activity</div>
            {activity.length === 0 && <div style={{ fontSize: 11, color: "#9a9aa4", padding: "6px 0" }}>No activity yet — capture master data to get started.</div>}
            {activity.slice(0, 5).map((a) => (
              <div className="act" key={a.id}>
                <span className="act-dot" style={{ background: actDot(a.action), marginTop: 5 }} />
                <div><div className="act-t"><b>{a.user_name || "System"}</b> {a.detail || a.action}</div>
                  <div className="act-time">{timeAgo(a.created_at)}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
